import {
  faCheckCircle,
  faChevronDown,
  faChevronUp,
  faDownload,
  faExclamationTriangle,
  faEye,
  faFileDownload,
  faFileExcel,
  faInfoCircle,
  faShieldAlt,
  faTimesCircle,
  faUpload,
  faUserCheck,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Badge, Button, Card, Grid, Loader, Progress, Stack, Table, TextInput } from "@mantine/core";
import React, { useRef, useState } from "react";
import bankDetailsApi, { BulkImportPreviewData, BulkImportResult, CSVRow } from "../../../data/api/bankDetailsApi";
import ErrorBoundary from "../../common/ErrorBoundary";

interface BulkBankDetailsImportProps {
  associationId: string;
  onImportComplete?: (result: any) => void;
  onClose?: () => void;
}

// Using imported types from bankDetailsApi

// Constants
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const BulkBankDetailsImport: React.FunctionComponent<BulkBankDetailsImportProps> = ({
  associationId,
  onImportComplete,
  onClose
}) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Import, 4: Results
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<BulkImportPreviewData | null>(null);
  const [parsedData, setParsedData] = useState<CSVRow[] | null>(null);
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null);
  const [reason, setReason] = useState("");
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.tsv')) {
      setError('Please select a CSV or TSV file');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  // Handle drag and drop
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    // Only set dragOver to false if we're leaving the drop zone itself, not child elements
    if (event.currentTarget === event.target) {
      setDragOver(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.csv') && !file.name.endsWith('.tsv')) {
        setError('Please select a CSV or TSV file');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  // Preview import (validation only)
  const handlePreview = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);

    try {
      const previewData = await bankDetailsApi.previewBulkImport(selectedFile, associationId);

      setPreviewData(previewData);
      setParsedData(previewData.parsedData); // Store parsed data for reuse
      setStep(2);
    } catch (err: any) {
      // Check if this is a validation error with validation report
      if (err.validationReport) {
        // Display validation errors in the preview
        setPreviewData({
          totalRecords: err.validationReport.totalRecords || 0,
          validationResult: err.validationReport,
          sampleValidRecords: [],
          stats: {
            successRate: 0,
            commonErrors: []
          },
          parsedData: [] // Empty parsed data for validation errors
        });
        setParsedData([]); // No parsed data when validation fails
        setStep(2);
      } else {
        setError(err.message || 'Preview failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Perform actual import
  const handleImport = async () => {
    if (!parsedData || parsedData.length === 0) {
      setError('No data to import. Please go back and upload a valid CSV file.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await bankDetailsApi.bulkImportFromData(parsedData, associationId, reason);

      // The result is directly the BulkImportResult, not wrapped in success/data
      setImportResult(result);
      setStep(4);
      if (onImportComplete) {
        onImportComplete(result);
      }
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  // Reset to start
  const handleReset = () => {
    setStep(1);
    setSelectedFile(null);
    setPreviewData(null);
    setImportResult(null);
    setError(null);
    setReason("");
    setShowErrorDetails(false);
    setExpandedErrors(new Set());
    setDownloadingTemplate(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Download CSV template
  const handleDownloadTemplate = async (filter: 'all' | 'without-bank-details') => {
    setDownloadingTemplate(true);
    setError(null);

    try {
      await bankDetailsApi.downloadCSVTemplate(associationId, filter);
    } catch (err: any) {
      setError(`Template download failed: ${err.message}`);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Toggle error details
  const toggleErrorDetails = (errorIndex: number) => {
    const newExpanded = new Set(expandedErrors);
    if (newExpanded.has(errorIndex)) {
      newExpanded.delete(errorIndex);
    } else {
      newExpanded.add(errorIndex);
    }
    setExpandedErrors(newExpanded);
  };

  // Export validation errors as CSV
  const exportValidationErrors = () => {
    if (!previewData?.validationResult.errors) return;

    const csvContent = [
      ['Row', 'External ID', 'Error Type', 'Error Details'].join(','),
      ...previewData.validationResult.errors.flatMap(error =>
        error.errors.map(errorMsg => [
          error.row,
          error.externalIdentifier,
          getErrorType(errorMsg),
          `"${errorMsg.replace(/"/g, '""')}"`
        ].join(','))
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bank_details_validation_errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const getErrorType = (errorMsg: string): string => {
    if (errorMsg.toLowerCase().includes('bsb')) return 'BSB';
    if (errorMsg.toLowerCase().includes('account number')) return 'Account Number';
    if (errorMsg.toLowerCase().includes('account name')) return 'Account Name';
    if (errorMsg.toLowerCase().includes('person')) return 'Person Not Found';
    if (errorMsg.toLowerCase().includes('duplicate')) return 'Duplicate';
    return 'Other';
  };

  // Render file upload step
  const renderUploadStep = () => (
    <Card>
      <Card.Section withBorder p="md">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faUpload} className="me-2" />
          Step 1: Upload CSV File
        </h5>
      </Card.Section>
      <div style={{ padding: "1rem" }}>
        {/* Security Notice */}
        <Alert color="blue" className="mb-4">
          <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
          <strong>Secure Import:</strong> Your CSV file is processed in-memory only.
        </Alert>

        {/* CSV Format Requirements */}
        <Card className="mb-4" style={{ backgroundColor: 'var(--color-bg-disabled-card)', border: '1px solid var(--bs-border-color)' }}>
          <div style={{ padding: "1rem" }}>
            <h6><FontAwesomeIcon icon={faInfoCircle} className="me-2" />Required CSV Format:</h6>
            <div className="mt-2">
              <code>externalIdentifier,bsb,accountNumber,accountName</code>
            </div>
            <small className="text-muted mt-1 d-block">
              • Headers are required<br />
              • Maximum 400 records per import<br />
              • BSB format: 123-456 or 123456<br />
              • Account numbers: 5-9 digits<br />
              • File size limit: {MAX_FILE_SIZE_MB}MB
            </small>
          </div>
        </Card>

        {/* CSV Template Download */}
        <Card className="mb-4">
          <Card.Section withBorder p="md">
            <h6 className="mb-0">
              <FontAwesomeIcon icon={faFileDownload} className="me-2" />
              Download CSV Template
            </h6>
          </Card.Section>
          <div style={{ padding: "1rem" }}>
            <p className="text-muted small mb-3">
              Download a pre-filled CSV template with persons from your association.
              The template includes external identifiers and display names for reference.
            </p>

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Button
                  color="blue" variant="outline"
                  onClick={() => handleDownloadTemplate('all')}
                  disabled={downloadingTemplate}
                  fullWidth
                >
                  {downloadingTemplate ? (
                    <Loader className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                  )}
                  All Persons
                </Button>
                <small className="text-muted d-block mt-1">
                  Download template for all persons in association
                </small>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Button
                  color="green" variant="outline"

                  onClick={() => handleDownloadTemplate('without-bank-details')}
                  disabled={downloadingTemplate}
                  fullWidth
                >
                  {downloadingTemplate ? (
                    <Loader className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                  )}
                  Without Bank Details
                </Button>
                <small className="text-muted d-block mt-1">
                  Only persons missing bank details
                </small>
              </Grid.Col>
            </Grid>

            <Card className="mt-3 mb-0" style={{ backgroundColor: "var(--color-bg-disabled-card)" }}>
              <div style={{ padding: "1rem" }} className="py-2">
                <small>
                  <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                  The template includes a <code>displayNameUnused</code> column for reference only.
                  Fill in the <code>bsb</code>, <code>accountNumber</code>, and <code>accountName</code> columns.
                </small>
              </div>
            </Card>
          </div>
        </Card>

        {/* File Upload Area */}
        <div
          className={`border-2 border-dashed p-4 text-center transition-all ${dragOver
            ? 'border-primary bg-primary bg-opacity-10 border-3'
            : selectedFile
              ? 'border-success bg-light'
              : 'border-secondary highlightOnHover:border-info'
            }`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            transform: dragOver ? 'scale(1.02)' : 'scale(1)'
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.tsv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {dragOver ? (
            <div>
              <FontAwesomeIcon icon={faUpload} size="3x" className="text-primary mb-2" />
              <p className="mb-1 fw-bold text-primary">Drop your CSV file here!</p>
              <p className="text-primary small">Release to upload</p>
            </div>
          ) : selectedFile ? (
            <div>
              <FontAwesomeIcon icon={faCheckCircle} size="2x" className="text-success mb-2" />
              <p className="mb-1"><strong>{selectedFile.name}</strong></p>
              <p className="text-muted small">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
              <Button

                color="gray" variant="outline"
                className="mt-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                  setError(null);
                  setParsedData(null); // Clear parsed data when removing file
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              >
                <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                Remove
              </Button>
            </div>
          ) : (
            <div>
              <FontAwesomeIcon icon={faFileExcel} size="2x" className="text-muted mb-2" />
              <p className="mb-1">Click to select CSV file or drag and drop</p>
              <p className="text-muted small">CSV or TSV files only, max {MAX_FILE_SIZE_MB}MB</p>
            </div>
          )}
        </div>

        {error && (
          <Alert color="red" className="mt-3">
            {error}
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="d-flex justify-content-between mt-4">
          <Button
            color="gray"
            onClick={() => {
              // Clear all form state and reset to initial step
              setSelectedFile(null);
              setError(null);
              setPreviewData(null);
              setParsedData(null); // Clear parsed data
              setImportResult(null);
              setReason("");
              setStep(1);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
              if (onClose) onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={handlePreview}
            disabled={!selectedFile || loading}
          >
            {loading ? (
              <>
                <Loader className="me-2" />
                Validating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faEye} className="me-2" />
                Preview & Validate
              </>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );

  // Render preview step
  const renderPreviewStep = () => (
    <Card>
      <Card.Section withBorder p="md">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faEye} className="me-2" />
          Step 2: Preview & Validation Results
        </h5>
      </Card.Section>
      <div style={{ padding: "1rem" }}>
        {previewData && (
          <>
            {/* Summary Stats */}
            <Grid className="mb-4 g-2">
              <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
                <div className="text-center p-3 border rounded h-100">
                  <h4 className="mb-1" style={{ color: 'var(--bs-primary)' }}>{previewData.totalRecords}</h4>
                  <small className="text-muted">Total Records</small>
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
                <div className="text-center p-3 border rounded h-100">
                  <h4 className="mb-1" style={{ color: 'var(--bs-success)' }}>{previewData.validationResult.validRecords}</h4>
                  <small className="text-muted">Valid Records</small>
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
                <div className="text-center p-3 border rounded h-100">
                  <h4 className="mb-1" style={{ color: 'var(--bs-danger)' }}>{previewData.validationResult.invalidRecords}</h4>
                  <small className="text-muted">Invalid Records</small>
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
                <div className="text-center p-3 border rounded h-100">
                  <h4 className="mb-1" style={{ color: 'var(--bs-info)' }}>{previewData.stats.successRate}%</h4>
                  <small className="text-muted">Success Rate</small>
                </div>
              </Grid.Col>
            </Grid>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Validation Progress</span>
                <Badge
                  color={previewData.validationResult.valid ? 'success' : 'warning'}
                >
                  {previewData.validationResult.valid ? 'All Valid' : `${previewData.validationResult.invalidRecords} Errors`}
                </Badge>
              </div>
              <Progress
                color={previewData.stats.successRate === 100 ? 'success' : 'warning'}
                value={previewData.stats.successRate}
                className="mb-2"
              />
            </div>

            {/* Validation Errors */}
            {!previewData.validationResult.valid && (
              <>
                <Alert color="yellow" className="mb-3">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <strong>Validation Failed:</strong> {previewData.validationResult.invalidRecords} records have errors.
                  All errors must be fixed before importing.
                </Alert>

                {/* Error Summary */}
                <Card className="mb-3">
                  <Card.Section withBorder p="md">
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Error Summary</span>
                      <Button
                        color="gray" variant="outline"

                        onClick={exportValidationErrors}
                      >
                        <FontAwesomeIcon icon={faDownload} className="me-1" />
                        Export Errors
                      </Button>
                    </div>
                  </Card.Section>
                  <div style={{ padding: "1rem" }}>
                    <Grid>
                      {previewData.stats.commonErrors.map((error, index) => (
                        <Grid.Col span={{ base: 12, sm: 6, md: 4 }} key={index} className="mb-2">
                          <div className="d-flex justify-content-between">
                            <span>{error.type}:</span>
                            <Badge color="red">{error.count}</Badge>
                          </div>
                        </Grid.Col>
                      ))}
                    </Grid>
                  </div>
                </Card>

                {/* Detailed Errors */}
                <Card>
                  <Card.Section withBorder p="md">
                    <Button
                      color="blue" variant="subtle"
                      onClick={() => setShowErrorDetails(!showErrorDetails)}
                      className="p-0 text-decoration-none"
                    >
                      <FontAwesomeIcon
                        icon={showErrorDetails ? faChevronUp : faChevronDown}
                        className="me-2"
                      />
                      Detailed Errors ({previewData.validationResult.errors.length} rows)
                    </Button>
                  </Card.Section>
                  {showErrorDetails && (
                    <div style={{ padding: "1rem", maxHeight: '400px', overflowY: 'auto' }} className="p-0">
                      <Table striped className="mb-0">
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>External ID</th>
                            <th>Errors</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.validationResult.errors.map((error, index) => (
                            <tr key={index}>
                              <td>{error.row}</td>
                              <td><code>{error.externalIdentifier}</code></td>
                              <td>
                                {error.errors.length === 1 ? (
                                  <span style={{ color: 'var(--bs-danger)' }} className="small">{error.errors[0]}</span>
                                ) : (
                                  <div>
                                    <Button
                                      color="blue" variant="subtle"

                                      className="p-0"
                                      style={{ color: 'var(--bs-danger)' }}
                                      onClick={() => toggleErrorDetails(index)}
                                    >
                                      {error.errors.length} errors
                                      <FontAwesomeIcon
                                        icon={expandedErrors.has(index) ? faChevronUp : faChevronDown}
                                        className="ms-1"
                                      />
                                    </Button>
                                    {expandedErrors.has(index) && (
                                      <ul className="list-unstyled small mt-1 mb-0">
                                        {error.errors.map((err, errIndex) => (
                                          <li key={errIndex} style={{ color: 'var(--bs-danger)' }}>• {err}</li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card>
              </>
            )}

            {/* Sample Valid Records */}
            {previewData.sampleValidRecords.length > 0 && (
              <Card className="mt-3">
                <Card.Section withBorder p="md">
                  <h6 className="mb-0">Sample Valid Records (First 5)</h6>
                </Card.Section>
                <div style={{ padding: "1rem" }}>
                  <Table className="mb-0">
                    <thead>
                      <tr>
                        <th>External ID</th>
                        <th>BSB</th>
                        <th>Account</th>
                        <th>Name</th>
                        <th>Person</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.sampleValidRecords.map((record, index) => (
                        <tr key={index}>
                          <td><code style={{ color: 'var(--bs-primary)' }}>{record.externalIdentifier}</code></td>
                          <td><span className="fw-bold">{record.bsb}</span></td>
                          <td><code style={{ color: 'var(--bs-secondary)' }}>{record.accountNumber}</code></td>
                          <td>{record.accountName}</td>
                          <td>
                            <Badge color={record.personExists ? 'success' : 'danger'}>
                              {record.personExists ? 'Found' : 'Not Found'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card>
            )}
          </>
        )}

        {/* Action Buttons */}
        <div className="mt-4">
          {/* Large Desktop Layout (xl and up) */}
          <div className="d-none d-xl-flex justify-content-between align-items-center">
            <Button color="gray" onClick={() => {
              setStep(1);
              // Clear preview data when going back, but keep parsed data for re-validation
            }}>
              <FontAwesomeIcon icon={faUpload} className="me-2" />
              Back to Upload
            </Button>

            {previewData?.validationResult.valid ? (
              <div className="d-flex align-items-center">
                <Stack gap="sm" className="me-3 mb-0">
                  <TextInput
                    type="text"
                    placeholder="Reason for import (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    style={{ width: '280px', minWidth: '200px' }}
                  />
                </Stack>
                <Button
                  color="green"
                  onClick={handleImport}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader className="me-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Import {previewData.totalRecords} Records
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <Alert color="yellow" className="mb-0">
                Fix all validation errors before importing
              </Alert>
            )}
          </div>

          {/* Medium Desktop Layout (lg to xl) - Stacked */}
          <div className="d-none d-lg-block d-xl-none">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Button color="gray" onClick={() => {
                setStep(1);
                // Clear preview data when going back, but keep parsed data for re-validation
              }}>
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Back to Upload
              </Button>
            </div>

            {previewData?.validationResult.valid ? (
              <div>
                <Stack gap="sm" className="mb-3">
                  <TextInput
                    type="text"
                    placeholder="Reason for import (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </Stack>
                <Button
                  color="green"
                  onClick={handleImport}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? (
                    <>
                      <Loader className="me-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Import {previewData.totalRecords} Records
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <Alert color="yellow" className="mb-0">
                Fix all validation errors before importing
              </Alert>
            )}
          </div>

          {/* Mobile/Tablet Layout */}
          <div className="d-block d-lg-none">
            <Button
              color="gray"
              onClick={() => {
                setStep(1);
                // Clear preview data when going back, but keep parsed data for re-validation
              }}
              className="w-100 mb-3"
            >
              <FontAwesomeIcon icon={faUpload} className="me-2" />
              Back to Upload
            </Button>

            {previewData?.validationResult.valid ? (
              <div>
                <Stack gap="sm" className="mb-3">
                  <TextInput
                    type="text"
                    placeholder="Reason for import (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </Stack>
                <Button
                  color="green"
                  onClick={handleImport}
                  disabled={loading}
                  className="w-100"
                >
                  {loading ? (
                    <>
                      <Loader className="me-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Import {previewData.totalRecords} Records
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <Alert color="yellow" className="mb-0">
                Fix all validation errors before importing
              </Alert>
            )}
          </div>
        </div>
      </div>
    </Card>
  );

  // Render results step
  const renderResultsStep = () => {
    const isSuccess = importResult && importResult.failedImports === 0;

    return (
      <Card>
        <Card.Section withBorder p="md" className={isSuccess ? "bg-success text-white" : "bg-warning text-dark"}>
          <h5 className="mb-0">
            <FontAwesomeIcon icon={isSuccess ? faCheckCircle : faExclamationTriangle} className="me-2" />
            Import {isSuccess ? 'Complete' : 'Complete with Errors'}
          </h5>
        </Card.Section>
        <div style={{ padding: "1rem" }}>
          {importResult && (
            <>
              {/* Results Summary */}
              <Grid className="mb-4 g-2">
                <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
                  <div className="text-center p-3 border rounded h-100">
                    <h4 className="mb-1" style={{ color: 'var(--bs-info)' }}>{importResult.processedRecords}</h4>
                    <small className="text-muted">Processed</small>
                  </div>
                </Grid.Col>
                <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
                  <div className="text-center p-3 border rounded h-100">
                    <h4 className="mb-1" style={{ color: 'var(--bs-success)' }}>{importResult.successfulImports}</h4>
                    <small className="text-muted">Successful</small>
                  </div>
                </Grid.Col>
                <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
                  <div className="text-center p-3 border rounded h-100">
                    <h4 className="mb-1" style={{ color: 'var(--bs-danger)' }}>{importResult.failedImports}</h4>
                    <small className="text-muted">Failed</small>
                  </div>
                </Grid.Col>
                <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
                  <div className="text-center p-3 border rounded h-100">
                    <h4 className="mb-1" style={{ color: 'var(--bs-warning)' }}>{importResult.skippedRecords}</h4>
                    <small className="text-muted">Skipped</small>
                  </div>
                </Grid.Col>
              </Grid>

              {/* Success/Status Message */}
              <Alert color={isSuccess ? "success" : "warning"} className="mb-3">
                <FontAwesomeIcon icon={isSuccess ? faCheckCircle : faExclamationTriangle} className="me-2" />
                <strong>{isSuccess ? 'Import Successful!' : 'Import Completed with Issues'}</strong> {importResult.successfulImports} bank details records have been imported{importResult.failedImports > 0 ? `, ${importResult.failedImports} failed` : ''}.
              </Alert>

              {/* Processing Errors */}
              {importResult.processingErrors.length > 0 && (
                <Alert color="yellow">
                  <h6><FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />Processing Errors:</h6>
                  <ul className="mb-0">
                    {importResult.processingErrors.map((error, index) => (
                      <li key={index}>
                        <strong>{error.externalIdentifier}:</strong> {error.error}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
            </>
          )}

          {/* Action Buttons */}
          <div className="mt-4">
            {/* Desktop Layout */}
            <div className="d-none d-sm-flex justify-content-between">
              <Button color="blue" onClick={handleReset}>
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Import More Records
              </Button>
              <Button color="green" onClick={onClose}>
                Done
              </Button>
            </div>

            {/* Mobile Layout */}
            <div className="d-block d-sm-none">
              <Button color="blue" onClick={handleReset} className="w-100 mb-2">
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Import More Records
              </Button>
              <Button color="green" onClick={onClose} className="w-100">
                Done
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <ErrorBoundary>
      <div className="bulk-bank-details-import">
        <div className="mb-4">
          <h4>Bulk Bank Details Import</h4>
          <p className="text-muted">
            Securely import bank details for multiple users from a CSV file.
            All data is validated before processing.
          </p>
        </div>

        {/* Progress Steps */}
        <Grid className="justify-content-center mb-4">
          <Grid.Col span={{ base: 12, sm: "auto", md: 6 }}>
            <div className="d-flex align-items-center">
              {[1, 2, 3].map((stepNum, index) => {
                const isActive = (step === stepNum) || (step === 4 && stepNum === 3);
                const isCompleted = step > stepNum || (step === 4 && stepNum >= 2);

                return (
                  <div key={stepNum} className="d-flex align-items-center">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: isActive ? 'var(--bs-primary)' : 'var(--color-bg-disabled-card)',
                        color: isActive ? 'white' : 'var(--color-text-medium)',
                        border: isActive ? '2px solid var(--bs-primary)' : '1px solid var(--color-divider)',
                        fontWeight: isActive ? 'bold' : 'normal',
                        boxShadow: isActive ? '0 0 0 3px rgba(var(--bs-primary-rgb), 0.25)' : 'none'
                      }}
                    >
                      {stepNum}
                    </div>
                    <div
                      className="ms-2"
                      style={{
                        color: isActive ? 'var(--bs-primary)' : 'var(--color-text-medium)',
                        fontWeight: isActive ? 'bold' : 'normal'
                      }}
                    >
                      {stepNum === 1 && 'Upload'}
                      {stepNum === 2 && 'Preview'}
                      {stepNum === 3 && 'Results'}
                    </div>
                    {index < 2 && (
                      <div
                        className="mx-3"
                        style={{
                          width: '50px',
                          height: '2px',
                          backgroundColor: isCompleted ? 'var(--bs-primary)' : 'var(--color-bg-disabled-card)',
                          border: isCompleted ? 'none' : '1px solid var(--color-divider)'
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </Grid.Col>
        </Grid>

        {/* Step Content */}
        {step === 1 && renderUploadStep()}
        {step === 2 && renderPreviewStep()}
        {step === 4 && renderResultsStep()}
      </div>
    </ErrorBoundary>
  );
};

export default BulkBankDetailsImport;
